import { Injectable } from '@angular/core';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  shareReplay,
  switchMap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { NetworkHealthService } from '../../../services/network-health.service';
import { ProductDbService } from '../../../services/productdb.service';
import { environment } from '../../app.environment';

/**
 * BillNumberRefreshService
 *
 * Keeps the dashboard "Bill No" flip-counter up to date over REST.
 * Replaces the STOMP `/topic/sales-count/{clientId}/{storeId}` WebSocket that
 * DashboardRightPanelComponent and RestaurantBillPanelComponent used before.
 *
 * The latest bill number is fetched (`GET /sale/getlatestbillno`) when:
 *   - a panel subscribes (initial load),
 *   - this counter finishes a sale / return invoice (requestRefresh()),
 *   - every POLL_INTERVAL_MS, so sales on other counters show up,
 *   - the browser tab becomes visible again.
 *
 * Ticks are skipped while the tab is hidden or the network is OFFLINE.
 * Polling only runs while at least one panel is subscribed.
 *
 * Not providedIn 'root': ProductDbService is only provided at component level,
 * so this service is listed in DashboardComponent.providers. The bill panels
 * are rendered inside the dashboard template and share that instance.
 */
@Injectable()
export class BillNumberRefreshService {
  private readonly DEFAULT_POLL_INTERVAL_MS = 10000;
  private readonly MIN_POLL_INTERVAL_MS = 5000;
  private readonly REFRESH_DEBOUNCE_MS = 300;

  /**
   * Polling interval from environment.billNumberPollIntervalMs.
   * Falls back to the default if missing/invalid and never goes below the
   * minimum, so a bad value can't flood the API.
   */
  private readonly POLL_INTERVAL_MS = this.resolvePollInterval(
    environment.billNumberPollIntervalMs,
  );

  private readonly refreshTrigger$ = new Subject<void>();

  /** Latest bill number. Emits only when the value changes. */
  readonly billNumber$: Observable<number>;

  constructor(
    private readonly productService: ProductDbService,
    private readonly networkHealthService: NetworkHealthService,
  ) {
    const afterSale$ = this.refreshTrigger$.pipe(
      debounceTime(this.REFRESH_DEBOUNCE_MS),
    );
    const poll$ = timer(0, this.POLL_INTERVAL_MS).pipe(
      filter(() => !document.hidden),
    );
    const tabVisible$ = fromEvent(document, 'visibilitychange').pipe(
      filter(() => !document.hidden),
    );

    this.billNumber$ = merge(afterSale$, poll$, tabVisible$).pipe(
      withLatestFrom(this.networkHealthService.isOffline$),
      filter(([, isOffline]) => !isOffline),
      // switchMap: a refresh-after-sale cancels an older in-flight poll so the
      // counter always reflects the newest invoice.
      switchMap(() =>
        this.productService.getLatestBillNo().pipe(
          map((response) => Number(response?.data?.count ?? NaN)),
          // Catch inside switchMap so one failed request never stops polling.
          catchError((error) => {
            console.error('Failed to fetch latest bill number:', error);
            return EMPTY;
          }),
        ),
      ),
      filter((count) => Number.isFinite(count)),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Call after this counter successfully generates a sale or return invoice. */
  requestRefresh(): void {
    this.refreshTrigger$.next();
  }

  private resolvePollInterval(configuredMs: unknown): number {
    const intervalMs = Number(configuredMs);
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) {
      return this.DEFAULT_POLL_INTERVAL_MS;
    }
    return Math.max(intervalMs, this.MIN_POLL_INTERVAL_MS);
  }
}
